// Entry level multiplayer wiring.
//
// The RichText fields already join the entry's collab room on their own; this
// mixin makes the *editor* a participant too, which is what makes the feature
// behave sanely inside the entry editing flow:
//
//  * presence is shown for the whole entry, not per field
//  * the server's write-back bumps sys.version, so the local autosave of the
//    non-collaborative fields learns about it instead of running into a
//    version-mismatch conflict dialog
//  * publish/archive/unpublish tell every peer that sys changed
//  * pending changes are flushed before publishing and when leaving the editor
//
// This is also the seam where the remaining field types will be moved into the
// Y.Doc; nothing else in EntryEditor needs to know about collaboration.

// value import is dynamic so yjs and the websocket provider stay out of the main
// bundle: with the multiplayer flag off, none of it is fetched. The type import is
// erased at compile time and costs nothing.
import { markRaw } from 'vue'
import type { CollabStatus, CollabUser, EntryCollabSession } from '../lib/collab/session'
import { userName } from '../utils'

export default {
	inject: [ 'settings', 'spaceId', 'environment', 'permissions', 'eventBus' ],
	data: () => ({
		collabSession: null as EntryCollabSession | null,
		collabPeers: [] as CollabUser[],
		collabStatus: 'connecting' as CollabStatus,
		collabOff: [] as Function[],
	}),
	computed: {
		// Must stay in sync with Field.effectiveMultiplayer: if the fields run in
		// local mode, the entry must NOT join a room - `collabOwnsRichText` would
		// otherwise suppress the autosave of exactly those fields, and their edits
		// would never be persisted by anybody.
		multiplayer() {
			return !!this.settings?.multiplayerRte
				&& !!this.settings?.collabHost
				&& this.settings?.multiplayerRteConnect !== false
		},
		// everybody except me, for the avatar stack
		collabOthers() {
			return this.collabPeers.filter(p => !p.self)
		},
		/**
		 * True while the collab worker is the writer for the RichText fields.
		 *
		 * Only then does the local autosave skip them. The moment the socket drops,
		 * the ordinary client-side autosave takes over again, so an offline edit is
		 * still persisted instead of living only in memory.
		 *
		 * That cannot produce a lost update: the client PUT carries
		 * `X-Contentful-Version`, so a write the worker made in the meantime turns
		 * into the usual version-mismatch merge rather than a silent overwrite. And
		 * on reconnect Yjs merges the offline edits into the room, after which the
		 * worker's next write-back supersedes the snapshot - the document stays the
		 * source of truth.
		 */
		collabOwnsRichText() {
			if (!this.multiplayer || !this.collabSession) return false
			return this.collabStatus == 'synced' || this.collabStatus == 'saving'
		},
	},
	methods: {
		async initCollab() {
			if (!this.multiplayer || !this.model?.sys?.id) return

			const { acquireSession } = await import('../lib/collab/session')
			// the editor may have been closed again while the chunk was loading
			if (this._isDestroyed || !this.model?.sys?.id) return

			const me = this.permissions?.me
			const userId = me?.sys?.id ?? me?.id ?? 'unknown'

			// markRaw for the same reason as in RichTextEditorMultiplayer: Vue must not
			// walk into the Y.Doc, and through it into the Slate editors
			const session = markRaw(acquireSession({
				host: this.settings.collabHost,
				ref: {
					spaceId: this.spaceId,
					environment: this.environment,
					entryId: this.model.sys.id,
				},
				user: { id: userId, name: userName(userId) || 'Someone' },
				// Only claim read-only when we positively know: `iCanUpdate` returns
				// false while the policy is still loading, and claiming it wrongly locks
				// this connection out of the write-back for its whole life. The CMA
				// enforces the real permission on the PUT either way.
				readOnly: !!this.permissions?.mePolicy && !this.permissions.iCanUpdate(this.model),
			}))
			this.collabSession = session
			this.collabPeers = session.peers()
			this.collabStatus = session.status

			this.collabOff = [
				session.on('peers', peers => { this.collabPeers = peers }),
				session.on('status', status => {
					const wasOwner = this.collabOwnsRichText
					this.collabStatus = status
					// Handing the pen back to the local autosave: flush straight away so
					// whatever was typed just before the drop is not left unsaved while
					// the worker is unreachable.
					if (wasOwner && !this.collabOwnsRichText) this.entryAutoSave()
				}),
				session.on('saved', sys => this.onCollabSaved(sys)),
				session.on('entryChanged', message => this.onCollabEntryChanged(message)),
				session.on('saveError', message => {
					this.eventBus.$emit('addToastMessage', message.message ?? 'Could not save the rich text field.', 'error')
				}),
			]
		},
		/**
		 * The collab server persisted the rich text fields. Only `sys` changed for
		 * us - adopt the new version so our own autosave keeps working, and make
		 * sure the change detection does not read this as a local edit.
		 */
		onCollabSaved(sys: any) {
			if (!this.model?.sys || !sys?.version) return
			if (sys.version <= this.model.sys.version) return
			this.model.sys.version = sys.version
			this.model.sys.updatedAt = sys.updatedAt
			this.model.sys.updatedBy = sys.updatedBy
			if (this.lastModel?.sys) this.lastModel.sys.version = sys.version
		},
		onCollabEntryChanged({ sys, reason }: any) {
			if (!this.model?.sys || !sys?.version) return
			if (sys.version <= this.model.sys.version) return
			Object.assign(this.model.sys, sys)
			if (reason != 'external')
				this.eventBus.$emit('addToastMessage', `This entry was ${ reason } by someone else.`, 'info')
		},
		/** tell peers that we changed sys outside of the document */
		notifyCollab(reason: 'published' | 'unpublished' | 'archived' | 'external') {
			if (!this.collabSession || !this.model?.sys) return
			this.collabSession.entryChanged({ ...this.model.sys }, reason)
		},
		/** ask the server to write pending rich text changes right now */
		async flushCollab() {
			// while offline the local autosave owns these fields, and the publish
			// button is already disabled until it has run - nothing to flush
			if (!this.collabSession || !this.collabOwnsRichText) return
			this.collabSession.flush()
			// give the worker a moment to land the PUT before we publish
			await new Promise(r => setTimeout(r, 600))
		},
		destroyCollab() {
			for (const off of this.collabOff) off()
			this.collabOff = []
			this.collabSession?.release()
			this.collabSession = null
		},
	},
	beforeDestroy() {
		this.destroyCollab()
	},
}
